import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, ShoppingBag } from "lucide-react";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NOLDGO_URL = "https://noldgo.com/restaurants/c6369716-98f1-4462-a9e4-2b9c24324a10";
const inputCls = "w-full rounded-lg border border-[#EBDDC6] bg-[#FDF6EC] px-4 py-3 text-sm text-[#3B2A1E] placeholder-[#A08B74] outline-none transition-colors duration-300 focus:border-[#E07A2B]";
const labelCls = "mb-1.5 block text-xs font-medium uppercase tracking-wider text-[#A08B74]";

const OrderModal = ({ dish, onClose }) => {
  const { lang, t } = useLang();
  const unit = Number((dish.price * 1.25).toFixed(2));
  const [qty, setQty] = useState(1);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", pickup_time: "19:00", notes: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const dishName = lang === "fr" ? dish.name_fr : dish.name_nl;
  const total = (unit * qty).toFixed(2).replace(".", ",");
  const noldgoLink = `${NOLDGO_URL}?plat=${encodeURIComponent(dishName)}`;

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/orders`, { dish: dishName, quantity: qty, unit_price: unit, ...form, notes: form.notes || null });
      toast.success(t.order.success);
      onClose();
    } catch {
      toast.error(t.order.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[#3B2A1E]/50 p-4 backdrop-blur-sm" onClick={onClose} data-testid="order-modal">
      <div className="w-full max-w-md rounded-2xl border border-[#EBDDC6] bg-[#FFFBF3] p-6 shadow-[0_24px_60px_rgba(93,64,36,0.25)] sm:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-mono2 text-xs uppercase tracking-[0.25em] text-[#A9761F]">{t.order.title}</p>
            <h3 className="font-display mt-1 text-2xl font-semibold text-[#C25E18]" data-testid="order-dish-name">{dishName}</h3>
            <p className="mt-1 text-sm text-[#7A6753]">{unit.toFixed(2).replace(".", ",")} € / {lang === "fr" ? "portion" : "portie"}</p>
          </div>
          <button onClick={onClose} className="shrink-0 text-[#A08B74] transition-colors hover:text-[#E07A2B]" data-testid="order-close-button" aria-label="Fermer">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={submit} className="mt-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>{t.order.qty}</label>
              <select value={qty} onChange={(e) => setQty(Number(e.target.value))} className={inputCls} data-testid="order-qty-select">
                {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>{t.order.pickup}</label>
              <input required type="time" value={form.pickup_time} onChange={set("pickup_time")} className={inputCls} data-testid="order-time-input" />
            </div>
          </div>
          <div>
            <label className={labelCls}>{t.order.name} *</label>
            <input required value={form.name} onChange={set("name")} className={inputCls} data-testid="order-name-input" />
          </div>
          <div>
            <label className={labelCls}>{t.order.phone} *</label>
            <input required type="tel" value={form.phone} onChange={set("phone")} placeholder="+32 ..." className={inputCls} data-testid="order-phone-input" />
          </div>
          <div>
            <label className={labelCls}>{t.order.notes}</label>
            <textarea rows={2} value={form.notes} onChange={set("notes")} className={`${inputCls} resize-none`} data-testid="order-notes-input" />
          </div>

          <div className="flex items-center justify-between rounded-xl bg-[#F5E9D7] px-4 py-3">
            <span className="text-xs text-[#7A6753]">{t.order.total}</span>
            <span className="font-mono2 text-lg font-semibold text-[#C25E18]" data-testid="order-total">{total} €</span>
          </div>

          <button type="submit" disabled={sending} data-testid="order-submit-button"
            className="w-full rounded-full bg-[#E07A2B] py-3.5 text-sm font-semibold text-[#3B2A1E] transition-all duration-300 hover:bg-[#E89B4E] hover:shadow-[0_0_32px_rgba(224,122,43,0.35)] disabled:opacity-60">
            {sending ? t.order.sending : t.order.submit}
          </button>
          <a href={noldgoLink} target="_blank" rel="noopener noreferrer"
            className="block text-center text-xs text-[#A9761F] transition-colors hover:text-[#E07A2B]" data-testid="order-noldgo-link">
            <ShoppingBag size={11} className="mr-1 inline" /> {t.order.viaNoldgo}
          </a>
        </form>
      </div>
    </div>
  );
};

export default OrderModal;
